import _ from 'lodash'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { useCreateWaitingRoomMutation } from '@/core/services/waiting-room'
import { WaitingRoomFormData } from '@/features/waiting-room'
import { getApiErrorMessage } from '@/utils/errors'

export function useCreateWaitingRoomForm() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [createWaitingRoom, { isLoading: isCreating }] = useCreateWaitingRoomMutation()

  const initialValues = useMemo(
    () => ({
      avgWaitingTime: 15,
      allowPatientSelection: false,
      description: '',
      doctors: [],
      establishments: [],
      isPrioritized: false,
      name: '',
      openingHours: _.range(7).map((day) => ({
        day,
        periods: [],
      })),
      specialities: [],
      price: 0,
      paymentReceiverId: undefined,
      paymentDialogConfig: { enabled: false, showForRoles: [] },
      fairDistribution: { enabled: false, maxConcurrent: 1, cooldownSeconds: 60, strategy: 'cap' as const, offerTtlSeconds: 30, prominentOffer: false },
      readinessConfig: { enabled: false, prioritizeReady: false, checklistTemplateId: '' },
      coPresenceConfig: { enabled: false, allowAssign: false },
    }),
    []
  )

  const onSubmit = async (values: WaitingRoomFormData) => {
    if (values.paymentReceiverId === '') values.paymentReceiverId = undefined
    if (values.readinessConfig.checklistTemplateId === '') values.readinessConfig.checklistTemplateId = undefined
    try {
      const waitingRoom = await createWaitingRoom({
        ...values,
        specialities: _.map(values.specialities, (speciality) => _.pick(speciality, ['code', 'name', 'selectByDefault'])),
      }).unwrap()
      enqueueSnackbar(t('notification.waitingRoom.create.success'), { variant: 'success' })
      navigate(`/waiting-rooms/${waitingRoom._id}`)
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.waitingRoom.create.error')), { variant: 'error' })
    }
  }

  return { initialValues, onSubmit, isSubmitting: isCreating }
}
